// Every dashboard fetch lives here so the X-mAiSight-Key header becomes a
// one-line change when the auth stream lands.

import { useQuery, type UseQueryResult } from "@tanstack/react-query";

const API_BASE =
  (import.meta.env?.VITE_API_BASE as string | undefined) ??
  "http://localhost:8000";

export interface TermMetric {
  doc_id: string;
  term_id: string;
  date: string;
  hover_count: number;
  unique_sessions: number;
  median_dwell_ms: number | null;
  abandonment_within_5s: number;
  share_of_section_views: number | null;
  /** USAGE: `hover_count / unique_sessions`. `null` when no visit opened the
   *  term — an absent denominator, not a rate of zero. Replaced
   *  `confusion_score`, which summed dwell-weighted hovers with a 5x
   *  abandonment term and is gone for good. */
  lookups_per_session: number | null;
}

export interface DocMetric {
  doc_id: string;
  date: string;
  unique_sessions: number;
  median_session_length_ms: number | null;
  bounced_share: number | null;
}

export interface HealthFinding {
  kind: "stalls" | "inconsistent" | "unexplained";
  term: string;
  doc_ids: string[];
  detail: string;
  score: number;
}

// The license key is the dashboard's only credential — there is no login.
// Held in localStorage rather than the URL so it stays out of browser
// history, referrers, and any proxy's access log. A deployed backend keeps
// `embed_allowed_origins` empty, so without a key every panel 404s; local
// dev resolves through that escape hatch and needs no key at all.
const KEY_STORAGE = "maisight_key";

export function getKey(): string | null {
  try {
    const stored = window.localStorage.getItem(KEY_STORAGE);
    if (stored) return stored;
  } catch {
    return null; // Safari private mode throws on localStorage access.
  }
  // `just dev` mints a license and passes it in so the dashboard opens
  // already authenticated. The DEV guard is load-bearing: Vite replaces it
  // with `false` in a production build and the branch is then stripped, so
  // no key can be baked into a shipped bundle even if the var is set.
  return import.meta.env.DEV
    ? ((import.meta.env?.VITE_MAISIGHT_KEY as string | undefined) ?? null)
    : null;
}

export function setKey(key: string): void {
  window.localStorage.setItem(KEY_STORAGE, key.trim());
}

function authHeaders(): Record<string, string> {
  const key = getKey();
  return key
    ? { Accept: "application/json", "X-mAiSight-Key": key }
    : { Accept: "application/json" };
}

// API_BASE is relative ("/api") in deployed builds, absolute in dev — URL
// needs an explicit base for the relative case.
function endpoint(path: string, days: number): string {
  const url = new URL(
    `${API_BASE}${path}`,
    typeof window !== "undefined" ? window.location.origin : undefined,
  );
  url.searchParams.set("days", String(days));
  return url.toString();
}

async function getJSON<T>(url: string): Promise<T> {
  const res = await fetch(url, { headers: authHeaders() });
  if (!res.ok) throw new Error(`GET ${url} failed: ${res.status}`);
  return (await res.json()) as T;
}

export function useTopTerms(
  orgSlug: string,
  days: number,
): UseQueryResult<TermMetric[], Error> {
  return useQuery<TermMetric[], Error>({
    queryKey: ["dashboard-terms", orgSlug, days],
    queryFn: async () =>
      (
        await getJSON<{ top_terms: TermMetric[] }>(
          endpoint(`/dashboards/${orgSlug}/terms`, days),
        )
      ).top_terms,
  });
}

export function useDocTimeline(
  orgSlug: string,
  days: number,
): UseQueryResult<DocMetric[], Error> {
  return useQuery<DocMetric[], Error>({
    queryKey: ["dashboard-docs", orgSlug, days],
    queryFn: async () =>
      (
        await getJSON<{ timeline: DocMetric[] }>(
          endpoint(`/dashboards/${orgSlug}/docs`, days),
        )
      ).timeline,
  });
}

// 404 means the health endpoint is not deployed for this org yet — an
// absent panel, not a failed dashboard.
export function useVocabularyHealth(
  orgSlug: string,
  days: number,
): UseQueryResult<HealthFinding[] | null, Error> {
  return useQuery<HealthFinding[] | null, Error>({
    queryKey: ["dashboard-health", orgSlug, days],
    queryFn: async () => {
      // corpus_id is required, and one corpus per org is the same assumption
      // reports/monthly.py makes (sources/schema.sql backfills
      // corpus_id = org_slug). Omitting it 422s, which reads here as a
      // permanently absent panel rather than a bug.
      const url = `${endpoint(`/dashboards/${orgSlug}/health`, days)}&corpus_id=${encodeURIComponent(orgSlug)}`;
      const res = await fetch(url, { headers: authHeaders() });
      if (res.status === 404) return null;
      if (!res.ok) throw new Error(`GET ${url} failed: ${res.status}`);
      return ((await res.json()) as { findings: HealthFinding[] }).findings;
    },
  });
}
